import { Link } from 'react-router-dom';
import { BidToken, BidText } from '../common/BidToken';
import { ComingSoonChip } from './ComingSoonChip';
import type { TopicDefinition, TopicIcon } from '../../config/moduleCatalog';

/**
 * One topic row on the inventory page (feature 023, FR-009/FR-010), per
 * `inventory.html`: bid glyph · title + module summary · medals · chevron.
 *
 * Disabled topics render as a NON-INTERACTIVE `<div>`, not a disabled link.
 * A link that goes nowhere is still announced as a link and still takes focus,
 * which is exactly the dead control FR-010 rules out — so the element type
 * changes rather than just its styling.
 *
 * The medal placeholders are decorative and empty: mastery tracking is not part
 * of this feature, and drawing filled medals would assert progress the system
 * does not measure. They carry `aria-hidden` so no one is told about a
 * progress indicator that has no data behind it.
 */

export interface InventoryRowProps {
  topic: TopicDefinition;
  /** Destination for enabled rows, e.g. `/inventory/precision/1c`. */
  to: string;
  /** Number of modules under the topic — drives the medal placeholders. */
  moduleCount: number;
}

// How each named catalog icon is drawn (client request, 2026-10-15). `transfer` is a pair of
// opposed arrows in the row's own brass — a text glyph, not an emoji, which on Windows came out
// as a blue tile against the cream-and-brass page. (The Preempts row and its 3S glyph left the
// inventories for a dashboard card on 2026-10-16 — see components/dashboard/PreemptDoorIcon.)
const TOPIC_ICONS: Record<TopicIcon, React.ReactNode> = {
  transfer: (
    <span className="text-[26px] text-brass" aria-hidden="true">
      ⇄
    </span>
  ),
};

function BidGlyph({ topic }: { topic: TopicDefinition }) {
  if (!topic.openingBid) {
    // A topic with no opening bid shows its own icon from the catalog (Transfers &
    // UMJOODO); the COMING SOON topics have none and keep the brain glyph from the reference design.
    return topic.icon ? <>{TOPIC_ICONS[topic.icon]}</> : <span aria-hidden="true">🧠</span>;
  }
  return (
    <>
      <BidToken bid={topic.openingBid} />
      {topic.alternateOpeningBid && (
        <>
          {' '}
          <BidToken bid={topic.alternateOpeningBid} />
        </>
      )}
    </>
  );
}

// On a phone in portrait the row runs out of width: the glyph column, the five
// medal placeholders and the chevron leave the title/summary a ~110px column, so
// "The 1♣ opening — 16+ points" wraps to four lines and the summary to five.
// `phone-pt:flex-wrap` lets the trailing group drop to its own line (see
// TRAILING below), which hands the whole row width back to the text. Tablets are
// out of scope on purpose — at >= 768px the single line still reads fine.
const ROW_BASE =
  'flex items-center gap-4 rounded-[10px] border border-line bg-card px-[18px] py-3.5 ' +
  'phone-pt:flex-wrap phone-pt:gap-y-2';

// Medals + chevron (or the COMING SOON chip) as ONE group, so the phone-portrait
// wrap puts them on the same second line rather than breaking between them. The
// inner gap-4 reproduces the spacing they had as direct row children, so nothing
// moves at tablet width and above.
const TRAILING = 'flex items-center gap-4 phone-pt:w-full phone-pt:justify-end';

export function InventoryRow({ topic, to, moduleCount }: InventoryRowProps) {
  const body = (
    <>
      <span className="min-w-[64px] font-serif text-[21px] font-bold text-ink phone-pt:min-w-0">
        <BidGlyph topic={topic} />
      </span>
      <span className="flex-1">
        <span className="block text-[14.5px] font-bold text-ink">
          <BidText text={topic.title} />
        </span>
        <span className="text-xs text-ink-soft">
          {topic.enabled ? `${moduleCount} modules · ${topic.subtitle}` : topic.subtitle}
        </span>
      </span>
      <span className={TRAILING}>
        {topic.enabled ? (
          <>
            <span className="flex items-center gap-1 text-[15px] opacity-25" aria-hidden="true">
              {Array.from({ length: moduleCount }, (_, i) => (
                <span key={i}>🏅</span>
              ))}
            </span>
            <span className="text-lg text-[#b8ae90]" aria-hidden="true">
              →
            </span>
          </>
        ) : (
          <ComingSoonChip />
        )}
      </span>
    </>
  );

  if (!topic.enabled) {
    // The reference design fades this row (`.invrow.dim{opacity:.55}`), but
    // opacity composites the text against the page and drops it below 4.5:1 —
    // axe rejected it. The row is instead set apart by a dashed border and the
    // cream (rather than card) fill, which reads as "not yet" without touching
    // the contrast. Same reasoning as FR-012's no-fading rule for Study/Master.
    return (
      <div
        className={`${ROW_BASE} border-dashed !bg-cream`}
        data-testid={`inventory-row-${topic.key}`}
      >
        {body}
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={`${ROW_BASE} no-underline transition-colors hover:border-brass focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2`}
      data-testid={`inventory-row-${topic.key}`}
    >
      {body}
    </Link>
  );
}
